import { useEffect, useState, useRef } from 'react';
import ReactToPrint from 'react-to-print';

import { Divider } from '@mui/material';
import { Box, Button, Grid, Typography, Stack } from '@mui/material';
import Slider from '@mui/material/Slider';

import {
  beforePrinting,
  afterPrinting,
  lerpColor,
} from '../../ReportScreens/utils';
import service from '../service/seatBeltEventResource';
import LiveCarReportInfo from '../../ReportScreens/components/live-car-report-info';
import SeatAndBelt from '../../ReportScreens/components/seat-and-belt';
import TitleGrid from '../../ReportScreens/components/TitleGrid';

const seatStatusChange = (eventStatus, seatStatus) => {
  if (eventStatus === 'ok' || eventStatus === 'true') {
    switch (seatStatus) {
      case 0:
        return 2;
      case 1:
        return 1;
      case 2:
        return 2;
      case 3:
        return 1;
      default:
        return 0;
    }
  } else if (eventStatus === 'none' || eventStatus === 'false') {
    switch (seatStatus) {
      case 0:
        return 0;
      case 1:
        return 3;
      case 2:
        return 0;
      case 3:
        return 3;
      default:
        return 0;
    }
  }
};

const beltStatusChange = (eventStatus, beltStatus) => {
  if (eventStatus === 'ok' || eventStatus === 'true') {
    switch (beltStatus) {
      case 0:
        return 3;
      case 1:
        return 1;
      case 2:
        return 1;
      case 3:
        return 3;
      default:
        return 0;
    }
  } else if (eventStatus === 'none' || eventStatus === 'false') {
    switch (beltStatus) {
      case 0:
        return 0;
      case 1:
        return 2;
      case 2:
        return 2;
      case 3:
        return 0;
      default:
        return 0;
    }
  }
};

function subtractHoursFromTimestamp(timestamp, hoursToSubtract) {
  // Create a Date object from the input timestamp
  const date = new Date(timestamp);

  // Subtract the specified number of hours
  date.setHours(date.getHours() - hoursToSubtract);

  // Get the hours and minutes
  const hours = date.getHours();
  const minutes = date.getMinutes();

  // Format the result as "HH:mm" (24-hour format)
  const formattedTime = `${hours < 10 ? '0' : ''}${hours}:${
    minutes < 10 ? '0' : ''
  }${minutes}`;

  return formattedTime;
}

const TransportationModal = (props) => {
  const [transportationReport, setTransportationReport] = useState({});
  const [loadCellEvents, setLoadCellEvents] = useState([{}]);
  const [beltSensorEvents, setBeltSensorEvents] = useState([{}]);
  const [betweenSelected, setBetweenSelected] = useState([]);
  const [concatEvents, setConcatEvents] = useState([]);
  const [times, setTimes] = useState([]);
  const [selectedTime, setSelectedTime] = useState(times[0] || null);
  const [seatStatuses, setSeatStatuses] = useState([]);

  const [carReportInfo, setCarReportInfo] = useState({
    onlyBelt: [],
    onlyLoad: [],
    both: [],
    anything: [],
  });

  const printRef = useRef(null);

  const getTransportationById = async () => {
    const result = await service.getTransportationReportByTransportationId(
      props.transportationId,
    );
    const data = result?.data;
    setTransportationReport(data?.data);
    setSeatStatuses(
      data?.data[0]?.data[0]?.data[0]?.data?.map((item) => {
        return {
          seatNo: item.seatNo,
          status: 0,
        };
      }),
    );
  };

  const getLoadCellEventsByTransportationId = async () => {
    const payload = {
      sort: 'time,asc',
    };

    const result = await service.getLoadCellEventsByTransportationId(
      props.transportationId,
      payload,
    );
    const data = result?.data;
    setLoadCellEvents(data?.data);
  };

  const getBeltSensorEventsByTransportationId = async () => {
    const payload = {
      sort: 'time,asc',
    };

    const result = await service.getBeltSensorEventsByTransportationId(
      props.transportationId,
      payload,
    );
    const data = result?.data;
    setBeltSensorEvents(data?.data);
  };

  const calculateBeltSensorAndLoadCellInformation = () => {
    let loadAndBeltInfo = {
      onlyBelt: [],
      onlyLoad: [],
      both: [],
      anything: [],
    };
    seatStatuses?.map((item) => {
      if (item.status === 0) {
        loadAndBeltInfo.anything.push(item);
      } else if (item.status === 1) {
        loadAndBeltInfo.both.push(item);
      } else if (item.status === 2) {
        loadAndBeltInfo.onlyLoad.push(item);
      } else if (item.status === 3) {
        loadAndBeltInfo.onlyBelt.push(item);
      }
    });

    setCarReportInfo(loadAndBeltInfo);
  };

  const sortEvents = (seatEvents, beltEvents) => {
    // Sorting and mapping logic here
    let sortedConcatEvents = [...loadCellEvents, ...beltSensorEvents].sort(
      (a, b) => new Date(a.time) - new Date(b.time),
    );
    setConcatEvents(sortedConcatEvents);

    let timeLabels = [];
    concatEvents.forEach((item) => {
      timeLabels.push({
        value: item.time,
        label: subtractHoursFromTimestamp(item.time, 3),
      });
    });
    setTimes(timeLabels);

    // Filter events based on the selected time
    const filteredEvents = sortedConcatEvents.filter(
      (item) => new Date(item.time) <= new Date(selectedTime?.value),
    );

    setBetweenSelected(filteredEvents);
  };

  const updateSeatStatuses = () => {
    // Reset seat statuses
    seatStatuses?.forEach((item) => {
      item.status = 0;
    });

    if (seatStatuses?.length > 0) {
      betweenSelected.forEach((item) => {
        seatStatuses.forEach((seat) => {
          if (item?.loadCell?.seat?.seatNo === seat.seatNo) {
            seat.status = seatStatusChange(item.status, seat.status);
          } else if (item?.beltSensor?.seat?.seatNo === seat.seatNo) {
            seat.status = beltStatusChange(item.status, seat.status);
          }
        });
      });
      // Update seat statuses
      setSeatStatuses([...seatStatuses]);
    }
  };

  const handleChange = (event, newValue) => {
    setSelectedTime(times[newValue]);
  };

  useEffect(() => {
    getTransportationById();
    getLoadCellEventsByTransportationId();
    getBeltSensorEventsByTransportationId();
  }, []);

  useEffect(() => {
    window.addEventListener('beforeprint', beforePrinting);
    window.addEventListener('afterprint', afterPrinting);
    return () => {
      window.removeEventListener('beforeinput', beforePrinting);
      window.removeEventListener('afterprint', afterPrinting);
    };
  }, []);

  useEffect(() => {
    sortEvents(beltSensorEvents, loadCellEvents);
  }, [loadCellEvents, beltSensorEvents, selectedTime]);

  useEffect(() => {
    updateSeatStatuses();
    calculateBeltSensorAndLoadCellInformation();
  }, [betweenSelected]);

  // console.log(seatStatuses, 'seatStatuses')
  // console.log(concatEvents, 'concatEvents')
  // console.log(betweenSelected, 'betweenSelected')
  // console.log(selectedTime, 'selectedTime')
  // console.log(times, 'times')

  return (
    <>
      <div>
        {/* button to trigger printing of target component */}
        <ReactToPrint
          trigger={() => <Button>PDF Olarak Dışa Aktar</Button>}
          content={() => printRef.current}
        />
      </div>

      <div
        ref={printRef}
        className="mt-6 "
        id="liveCarReport"
        style={{ textAlign: 'center' }}
      >
        {(beltSensorEvents.length > 0 || loadCellEvents.length > 0) && (
          <div className="mx-12">
            <Typography variant="h5" sx={{ marginBottom: '20px' }}>
              Görev Zaman Aralığı
            </Typography>
            <Slider
              marks={[
                {
                  value: 0,
                  label: times[0]?.label !== 'NaN:NaN' ? times[0]?.label : '',
                },
                {
                  value: Math.floor(times.length / 2),
                  label:
                    times[Math.floor(times.length / 2)]?.label !== 'NaN:NaN'
                      ? times[Math.floor(times.length / 2)]?.label
                      : '',
                },
                {
                  value: times.length - 1,
                  label:
                    times[times.length - 1]?.label !== 'NaN:NaN'
                      ? times[times.length - 1]?.label
                      : '',
                },
              ]}
              min={0}
              size="small"
              defaultValue={0}
              max={times.length - 1}
              onChange={handleChange}
              valueLabelDisplay="on"
              valueLabelFormat={(value) => {
                return times[value]?.label !== 'NaN:NaN'
                  ? times[value]?.label
                  : '';
              }}
            />
          </div>
        )}

        <TitleGrid
          title={
            transportationReport.length > 0
              ? transportationReport[0]?.plate
              : ''
          }
          title2={
            transportationReport.length > 0
              ? transportationReport[0]?.data[0]?.name +
                ' | ' +
                transportationReport[0]?.data[0]?.data[0]?.name
              : ''
          }
        />
        <TitleGrid
          title="Görev Raporu"
          title2="Transportation Report"
          widthRate={12}
        />

        <Grid
          sx={{
            border: 1,
            padding: 2,
            borderColor: '#2e3448',
            padding: '12px',
          }}
          container
          style={{
            marginTop: '10px',
          }}
        >
          <Grid
            style={{
              marginTop: 10,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            xs={7}
            item
          >
            <Grid
              style={{
                width: '600px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
              container
              className="mt-5  "
            >
              {transportationReport.length > 0 ? (
                seatStatuses?.map((item, index) => {
                  return (
                    <Grid
                      key={index}
                      item
                      xs={3}
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Stack direction={'column'}>
                        <Box
                          style={{
                            position: 'relative',
                          }}
                          width={'60px'}
                          className=" flex justify-center items-center"
                        >
                          <SeatAndBelt
                            belt={item.status === 1 || item.status === 3}
                            seat={item.status === 1 || item.status === 2}
                          />
                        </Box>
                        <Typography sx={{ fontSize: 18, fontWeight: '500' }}>
                          {item.seatNo}
                        </Typography>
                      </Stack>
                    </Grid>
                  );
                })
              ) : (
                <Grid item xs alignItems={'center'} justifyContent={'center'}>
                  <Typography sx={{ color: '#2e3448' }} variant="h6">
                    Veri Bulunamadı
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ mr: '-3px', borderColor: '#2e3448' }}
          />

          <Grid item xs={5}>
            <LiveCarReportInfo liveCarReportInfo={carReportInfo} />
          </Grid>
        </Grid>
      </div>
    </>
  );
};

export default TransportationModal;
